import { UserService } from '$core/services/user.service';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserAgreementUpdatingModalComponent } from './user-agreement-updating-modal.component';
import { UserAgreementUpdatingModalOutput } from './user-agreement-updating-modal-output';

/**
 * Routes the modal must never be shown on. The user agreement page itself is one
 * of them: it is opened from the modal to read the agreement, so asking for the
 * acceptance there again makes the text unreadable.
 */
const EXCLUDED_ROUTES: readonly string[] = ['/articles/portal-public-offer'];

/**
 * Single entry point for the user agreement updating modal.
 *
 * Whatever triggers the agreement request - a `403` with the
 * `user_agreement_not_accepted` code, the session state or the individual EPA
 * widget - only one modal is shown at a time. Everything waiting for the
 * agreement is notified through {@link accepted$} once it has been accepted.
 */
@Injectable({ providedIn: 'root' })
export class UserAgreementUpdatingService {
  private readonly modalService = inject(BsModalService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  private readonly acceptedSubject = new Subject<void>();

  private modalRef: BsModalRef<UserAgreementUpdatingModalComponent> | null =
    null;

  /**
   * Emits once the user agreement has been accepted.
   */
  public readonly accepted$: Observable<void> =
    this.acceptedSubject.asObservable();

  /**
   * Whether the modal is currently shown.
   */
  public get isOpen(): boolean {
    return !!this.modalRef;
  }

  /**
   * Requests the user agreement acceptance. Keeps the already shown modal if it
   * is open, so only one modal exists at a time.
   *
   * @param reload Optional callback invoked once the agreement is accepted, so
   * the caller may re-request the data which has been rejected.
   * @returns Whether the acceptance is actually being requested. `false` means
   * the current route is excluded, so the modal is not shown here, though the
   * agreement may still be accepted later once the user navigates away.
   */
  public request(reload?: () => void): boolean {
    if (this.isExcludedRoute) {
      return false;
    }

    if (reload) {
      this.acceptedSubject.pipe(take(1)).subscribe(() => reload());
    }

    if (this.modalRef) {
      return true;
    }

    this.modalRef = this.modalService.show(
      UserAgreementUpdatingModalComponent,
      {
        class: 'modal-dialog-centered',
        ignoreBackdropClick: true,
        keyboard: false,
      }
    );

    this.modalRef.onHide?.subscribe(
      (value: UserAgreementUpdatingModalOutput | unknown) => {
        this.modalRef = null;

        if (value === 'confirm') {
          this.accept();
        }
      }
    );

    return true;
  }

  /**
   * Whether the current route is excluded from showing the modal. The language
   * prefix is ignored, so the localized routes are excluded as well.
   */
  private get isExcludedRoute(): boolean {
    const url = this.router.url.split('?')[0].split('#')[0];

    return EXCLUDED_ROUTES.some((route) => url.endsWith(route));
  }

  private accept(): void {
    this.userService
      .updateUser({ is_accept_terms: true })
      .subscribe(() => this.acceptedSubject.next());
  }
}
